import { TaJson } from "ta-json";
import URI from "urijs";

import { NullableResultPromise } from "../base-types";
import { IOptionList } from "../contracts/base/option-lists/option-list";
import Guard from "../guard";
import { OptionListMapper } from "../mappers/option-list-mapper";
import OptionListCollectionResource from "../models/option-lists/option-list-collection-resource";
import OptionListDescriptionResource from "../models/option-lists/option-list-description-resource";
import OptionListResource from "../models/option-lists/option-list-resource";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { ResponseHandler } from "./response-handler";

export interface IOptionListClient {
    /**
     * Gets the names of all option lists that exist.
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    getAllNamesAsync(cancelCallback?: CancelCallback): Promise<Array<string>>;

    /**
     * Gets an array of all options list descriptions.
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    getAllDescriptionsAsync(cancelCallback?: CancelCallback): Promise<Array<OptionListDescriptionResource>>;

    /**
     * Gets the option list with the specified name.
     *
     * @param name - the option list name
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @param cacheSignature - Signature of the cached resources for controlling browser caching
     */
    getAsync(
        name: string,
        cancelCallback?: CancelCallback,
        cacheSignature?: string
    ): NullableResultPromise<IOptionList>;

    /**
     * Creates a new option list
     *
     * @param optionList - {@link IOptionList} - the option list instance
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    createAsync(optionList: IOptionList, cancelCallback?: CancelCallback): Promise<void>;

    /**
     * Update an existing option list
     *
     * @param optionList - {@link IOptionList} - the option list instance
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    updateAsync(optionList: IOptionList, cancelCallback?: CancelCallback): Promise<void>;

    /**
     * Delete an existing option list
     *
     * @param name - the name of the option list
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    deleteAsync(name: string, cancelCallback?: CancelCallback): Promise<void>;
}

export class OptionListClient implements IOptionListClient {
    private readonly _client: IExtendedContentHubClient;
    private readonly _mapper: OptionListMapper;

    constructor(client: IExtendedContentHubClient) {
        this._client = client;
        this._mapper = new OptionListMapper(client);
    }

    async getAllNamesAsync(cancelCallback?: CancelCallback): Promise<Array<string>> {
        const link = await this._client.linkHelper.dataSourcesLinkAsync();
        const response = await this._client.raw.getAsync(link.href, undefined, cancelCallback);
        const resource = TaJson.deserialize<OptionListCollectionResource>(
            response.content,
            OptionListCollectionResource
        );
        const names = resource.items.map(item => item.name);
        return names;
    }

    async getAllDescriptionsAsync(cancelCallback?: CancelCallback): Promise<Array<OptionListDescriptionResource>> {
        const link = await this._client.linkHelper.dataSourcesLinkAsync();
        const response = await this._client.raw.getAsync(link.href, undefined, cancelCallback);
        const resource = TaJson.deserialize<OptionListCollectionResource>(
            response.content,
            OptionListCollectionResource
        );

        return resource.items;
    }

    async getAsync(
        name: string,
        cancelCallback?: CancelCallback,
        cacheSignature?: string
    ): NullableResultPromise<IOptionList> {
        Guard.stringNotNullOrEmpty(name);

        const link = await this._client.linkHelper.dataSourceToLinkAsync(name);
        const uri = new URI(link.href);

        if (!uri.hasQuery("after") && cacheSignature) {
            uri.addQuery("after", cacheSignature);

            link.href = uri.toString();
        }

        const response = await this._client.raw.getAsync(link.href, undefined, cancelCallback);

        if (response.statusCode === 404) {
            return null;
        }

        ResponseHandler.handleErrors(response);

        const resource = TaJson.deserialize<OptionListResource>(response.content, OptionListResource);

        return this._mapper.mapOptionListAsync(resource);
    }

    async createAsync(optionList: IOptionList, cancelCallback?: CancelCallback): Promise<void> {
        Guard.notNull(optionList, "optionList");

        const resource = this._mapper.map(optionList);
        const link = await this._client.linkHelper.dataSourcesLinkAsync();
        const response = await this._client.raw.postAsync(
            link.href,
            TaJson.serialize(resource),
            undefined,
            cancelCallback
        );

        ResponseHandler.handleErrors(response);
    }

    async updateAsync(optionList: IOptionList, cancelCallback?: CancelCallback): Promise<void> {
        Guard.notNull(optionList, "optionList");

        const resource = this._mapper.map(optionList);
        const link = await this._client.linkHelper.dataSourceToLinkAsync(optionList.name);
        const response = await this._client.raw.putAsync(
            link.href,
            TaJson.serialize(resource),
            undefined,
            cancelCallback
        );

        ResponseHandler.handleErrors(response);
    }

    async deleteAsync(name: string, cancelCallback?: CancelCallback): Promise<void> {
        Guard.stringNotNullOrEmpty(name);

        const link = await this._client.linkHelper.dataSourceToLinkAsync(name);
        const response = await this._client.raw.deleteAsync(link.href, undefined, cancelCallback);

        ResponseHandler.handleErrors(response);
    }
}
